import guestApi from '../helpers/guestApi'
import { officeConstants } from './constants'
import * as THREE from 'three'
import { STLLoader } from 'three-stdlib'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import {
    collectMergedGeometryAndMaterials,
    ensureIndexedGeometry,
    prepareGltfSourceForRuntime,
} from '../containers/FenceModel/ModelViewer/modelGeometryOptimization'
import {
    buildGeometryPerformanceMetrics,
    prepareGeometryForFastRaycast,
    registerSceneModelPerformance,
} from '../containers/FenceModel/ModelViewer/scenePerformance'

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY ||
    'your-32-character-secret-key1234567890'

const decryptData = async (label, encryptedData) => {
    if (!encryptedData?.encrypted || !encryptedData?.iv) {
        return null
    }

    const key = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(ENCRYPTION_KEY.padEnd(32, '\0').slice(0, 32)),
        { name: 'AES-CBC', length: 256 },
        false,
        ['decrypt']
    )
    const encryptedUint8 = new Uint8Array(
        atob(encryptedData.encrypted).split('').map((char) => char.charCodeAt(0))
    )
    const ivUint8 = new Uint8Array(
        atob(encryptedData.iv).split('').map((char) => char.charCodeAt(0))
    )

    return crypto.subtle.decrypt(
        {
            name: 'AES-CBC',
            iv: ivUint8,
        },
        key,
        encryptedUint8
    )
}

const getGeometryDimensions = (geometry) => {
    geometry.computeBoundingBox()

    const size = new THREE.Vector3()
    geometry.boundingBox?.getSize(size)

    return {
        x: size.x.toFixed(2),
        y: size.y.toFixed(2),
        z: size.z.toFixed(2),
    }
}

const patchMtlTexturePaths = (mtlText, textureUrls = {}) => {
    return String(mtlText || '')
        .split(/\r?\n/)
        .map((line) => {
            const trimmed = line.trim()
            if (!trimmed) return line

            const tokens = trimmed.split(/\s+/)
            const command = String(tokens[0] || '').toLowerCase()
            const textureCommands = new Set([
                'map_kd',
                'map_ka',
                'map_ks',
                'map_ke',
                'map_bump',
                'bump',
                'norm',
                'disp',
                'decal',
                'refl',
                'map_d',
                'map_pr',
                'map_pm',
                'map_ns',
            ])

            if (!textureCommands.has(command)) return line

            const rawName = tokens[tokens.length - 1]
            const shortName = String(rawName || '').split('/').pop().split('\\').pop()
            const replacement = textureUrls[rawName] || textureUrls[shortName]

            if (!replacement) return line

            tokens[tokens.length - 1] = replacement
            return tokens.join(' ')
        })
        .join('\n')
}

const parseEncryptedModel = async (productId, encryptedData = {}) => {
    const totalStartedAt = performance.now()
    const modelBuffer = await decryptData(productId, encryptedData)

    if (!modelBuffer) {
        throw new Error('Сервер вернул повреждённую 3D-модель')
    }

    const fileType = String(encryptedData.fileType || 'stl').toLowerCase()
    const decodeStartedAt = performance.now()
    let decodeFinishedAt = decodeStartedAt
    let mergeStartedAt = decodeStartedAt
    let geometry = null
    let materials = null
    let sourceMeshCount = 1
    let sourcePrimitiveCount = 1

    if (fileType === 'stl') {
        geometry = new STLLoader().parse(modelBuffer)

        if (!geometry.getAttribute('normal')) {
            geometry.computeVertexNormals()
        }

        ensureIndexedGeometry(geometry)
        decodeFinishedAt = performance.now()
        mergeStartedAt = decodeFinishedAt
    } else if (fileType === 'obj') {
        const textureUrls = {}

        for (const [name, textureData] of Object.entries(encryptedData.textures || {})) {
            const textureBuffer = await decryptData(`${productId}:${name}`, textureData)
            if (!textureBuffer) continue

            const extension = String(name).split('.').pop()?.toLowerCase()
            const mime = extension === 'jpg' || extension === 'jpeg'
                ? 'image/jpeg'
                : extension === 'webp'
                    ? 'image/webp'
                    : 'image/png'
            const url = URL.createObjectURL(new Blob([textureBuffer], { type: mime }))
            textureUrls[name] = url
            textureUrls[String(name).split('/').pop().split('\\').pop()] = url
        }

        const objUrl = URL.createObjectURL(new Blob([modelBuffer], { type: 'text/plain' }))

        try {
            const objLoader = new OBJLoader()

            if (encryptedData.mtl) {
                const mtlBuffer = await decryptData(`${productId}:mtl`, encryptedData.mtl)

                if (mtlBuffer) {
                    const mtlText = patchMtlTexturePaths(
                        new TextDecoder().decode(mtlBuffer),
                        textureUrls
                    )
                    const prepared = new MTLLoader().parse(mtlText, '')
                    prepared.preload()
                    objLoader.setMaterials(prepared)
                }
            }

            const object = await new Promise((resolve, reject) => {
                objLoader.load(objUrl, resolve, undefined, reject)
            })

            decodeFinishedAt = performance.now()
            mergeStartedAt = decodeFinishedAt

            const merged = collectMergedGeometryAndMaterials(object)
            geometry = merged.geometry
            materials = merged.materials
            sourceMeshCount = merged.sourceMeshCount
            sourcePrimitiveCount = merged.sourcePrimitiveCount
        } finally {
            URL.revokeObjectURL(objUrl)
        }
    } else if (fileType === 'glb' || fileType === 'gltf') {
        const loader = new GLTFLoader()
        const dracoLoader = new DRACOLoader()
        const cores = typeof navigator !== 'undefined'
            ? Math.max(1, Number(navigator.hardwareConcurrency || 4))
            : 4

        dracoLoader.setDecoderPath('/draco/')
        dracoLoader.setWorkerLimit(Math.max(1, Math.min(4, Math.floor(cores / 2))))
        dracoLoader.preload()
        loader.setDRACOLoader(dracoLoader)

        try {
            const rawSource = fileType === 'gltf'
                ? new TextDecoder().decode(modelBuffer)
                : modelBuffer
            const source = prepareGltfSourceForRuntime(rawSource, fileType)
            const gltf = await new Promise((resolve, reject) => {
                loader.parse(source, '', resolve, reject)
            })

            decodeFinishedAt = performance.now()
            mergeStartedAt = decodeFinishedAt

            const merged = collectMergedGeometryAndMaterials(gltf.scene)
            geometry = merged.geometry
            materials = merged.materials
            sourceMeshCount = merged.sourceMeshCount
            sourcePrimitiveCount = merged.sourcePrimitiveCount
        } finally {
            dracoLoader.dispose()
        }
    } else {
        throw new Error(`Формат 3D-модели не поддерживается: ${fileType}`)
    }

    const finishedAt = performance.now()
    const performanceMetrics = buildGeometryPerformanceMetrics({
        geometry,
        modelId: productId,
        fileType,
        sourceBytes: Number(modelBuffer.byteLength || 0),
        decodeMs: decodeFinishedAt - decodeStartedAt,
        mergeMs: finishedAt - mergeStartedAt,
        materialCount: Array.isArray(materials) ? materials.length : (materials ? 1 : 0),
        sourceMeshCount,
        sourcePrimitiveCount,
    })

    registerSceneModelPerformance(productId, {
        ...performanceMetrics,
        totalLoadPreparationMs: finishedAt - totalStartedAt,
    })

    prepareGeometryForFastRaycast(geometry, {
        modelId: productId,
        defer: performanceMetrics.triangles >= 30000,
        maxLeafTris: performanceMetrics.triangles > 500000 ? 48 : 24,
    })

    if (performanceMetrics.triangles >= 250000) {
        console.info('[AO3D][HeavyModel]', {
            productId,
            fileType,
            triangles: performanceMetrics.triangles,
            vertices: performanceMetrics.vertices,
            indexed: performanceMetrics.indexed,
            geometryMB: Math.round(performanceMetrics.decodedGeometryBytes / 1024 / 1024),
            sourceMB: Math.round(performanceMetrics.sourceBytes / 1024 / 1024),
            decodeMs: Math.round(performanceMetrics.decodeMs),
            mergeMs: Math.round(performanceMetrics.mergeMs),
        })
    }

    return {
        geometry,
        materials,
        dimensions: getGeometryDimensions(geometry),
        fileType,
        performance: performanceMetrics,
    }
}

export const getSceneGuestProductModelData = (guestToken, productId) => {
    return async (dispatch) => {
        try {
            dispatch({
                type: officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS,
                payload: {
                    productId,
                    loaded: 0,
                    total: 1,
                    fullName: '',
                },
            })
            dispatch({ type: officeConstants.GET_PRODUCT_MODEL_DATA_REQUEST })

            const response = await guestApi.get(
                `/scene-guest-access/model/${productId}`,
                {
                    sceneGuestToken: guestToken,
                    onDownloadProgress: (event) => {
                        const loaded = Number(event?.loaded || 0)
                        const total = Number(event?.total || loaded || 1)

                        dispatch({
                            type: officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS,
                            payload: {
                                productId,
                                loaded,
                                total,
                                fullName: '',
                            },
                        })
                    },
                }
            )

            const parsed = await parseEncryptedModel(
                productId,
                response.data?.encryptedData || {}
            )
            const action = {
                type: officeConstants.GET_PRODUCT_MODEL_DATA_SUCCESS,
                payload: {
                    productId,
                    geometry: parsed.geometry,
                    materials: parsed.materials,
                    dimensions: parsed.dimensions,
                    textures: response.data?.encryptedData?.textures || {},
                    fileType: parsed.fileType,
                    performance: parsed.performance,
                },
            }

            dispatch(action)
            return action
        } catch (error) {
            const action = {
                type: officeConstants.GET_PRODUCT_MODEL_DATA_FAILURE,
                payload: {
                    productId,
                    error: error?.response?.data?.message ||
                        error?.message ||
                        'Ошибка загрузки 3D-модели',
                },
            }

            dispatch(action)
            throw error
        } finally {
            dispatch({
                type: officeConstants.CLEAR_PRODUCT_MODEL_MODEL_PROGRESS,
                payload: { productId },
            })
        }
    }
}
